import { Component } from '@theme/component';

/**
 * A custom element that turns the companion-products list into a one-row
 * horizontal slider.
 *
 * The Liquid block renders every companion as a fixed-width card inside a
 * scroll-snap track (ref="track"); this component only drives the prev/next
 * arrows and keeps their disabled state in sync with the scroll position.
 * The add-to-cart / quick-view behaviour inside each card is owned entirely
 * by the quick-add components and is untouched by scrolling.
 *
 * @typedef {object} CompanionProductsRefs
 * @property {HTMLElement} track - The scrollable card track.
 * @property {HTMLButtonElement} [prevButton] - Scroll-back arrow.
 * @property {HTMLButtonElement} [nextButton] - Scroll-forward arrow.
 *
 * @extends {Component<CompanionProductsRefs>}
 */
class CompanionProductsComponent extends Component {
  requiredRefs = ['track'];

  #resizeObserver = new ResizeObserver(() => this.#update());

  connectedCallback() {
    super.connectedCallback();
    this.refs.track.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver.observe(this.refs.track);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#update);
    this.#resizeObserver.disconnect();
    clearTimeout(this.#settleTimer);
  }

  previousPage = () => this.#scrollByCard(-1);
  nextPage = () => this.#scrollByCard(1);

  /** @param {number} direction - 1 forward, -1 back. */
  #scrollByCard(direction) {
    const { track } = this.refs;
    const card = track.querySelector('.companion-products__item');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const currentIndex = Math.round(track.scrollLeft / step);
    const target = Math.min(Math.max(0, (currentIndex + direction) * step), maxScroll);

    if (this.#reducedMotion) {
      track.scrollTo({ left: target, behavior: 'auto' });
      return;
    }

    this.#animateTo(target);
  }

  /**
   * Eased scroll to an absolute offset.
   *
   * The easing is done by CSS (`scroll-behavior: smooth`, applied only while
   * the animating attribute is set) rather than by scrollTo({behavior}) or a
   * rAF loop. Two reasons:
   *   - Chrome refuses a programmatic smooth scroll while scroll-snap is
   *     active on the container, so the press does nothing; the same attribute
   *     that turns easing on also suspends snap, and the forced style flush
   *     guarantees both land before the scroll is issued.
   *   - A rAF-driven tween stalls wherever rAF is throttled, degrading to a
   *     jump. A compositor-driven scroll keeps easing regardless.
   * Snap is restored on a timer once the animation has had time to finish, so
   * dragging still snaps to card boundaries afterwards.
   *
   * @param {number} target - final scrollLeft, in px.
   */
  #animateTo(target) {
    const { track } = this.refs;
    if (Math.abs(target - track.scrollLeft) < 1) return;

    track.dataset.gcAnimating = 'true';
    void track.offsetWidth;
    track.scrollLeft = target;

    clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(() => {
      delete track.dataset.gcAnimating;
      this.#update();
    }, 550);
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #settleTimer = 0;

  #update = () => {
    const { track, prevButton, nextButton } = this.refs;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const overflows = maxScroll > 1;

    this.toggleAttribute('data-overflowing', overflows);
    if (prevButton) prevButton.disabled = !overflows || track.scrollLeft <= 1;
    if (nextButton) nextButton.disabled = !overflows || track.scrollLeft >= maxScroll - 1;
  };
}

if (!customElements.get('companion-products-component')) {
  customElements.define('companion-products-component', CompanionProductsComponent);
}
